import { useState, useMemo, useCallback, useEffect } from 'react';
import { complianceApi } from '@/api/compliance.api';
import { useWarehouseStore } from '@/stores/warehouseStore';

function formatINR(n?: number | string | null): string {
  const num = Number(n);
  if (n == null || isNaN(num)) return '₹0.00';
  return `₹${num.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatDate(iso?: string | null): string {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return String(iso);
  }
}

interface Props {
  onClose: () => void;
  onGenerated: (newEWBs?: any[]) => void;
}

type Step = 1 | 2 | 3;

interface EwbGenerateResult {
  id: string;
  docNo: string;
  customer: string;
  status: 'SUCCESS' | 'FAILED';
  ewbNumber: string;
  validUntil?: string;
  error: string;
}

export default function BulkGenerateModal({ onClose, onGenerated }: Props) {
  const [step, setStep] = useState<Step>(1);
  const [docType, setDocType] = useState<'invoice' | 'challan'>('invoice');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [customerSearch, setCustomerSearch] = useState('');
  const [onlyAbove50k, setOnlyAbove50k] = useState(true);

  // Transport details (Step 2)
  const [transportMode, setTransportMode] = useState('1'); // 1: Road
  const [vehicleNumber, setVehicleNumber] = useState('');
  const [vehicleType, setVehicleType] = useState<'R' | 'O'>('R');
  const [transporterName, setTransporterName] = useState('');
  const [transporterId, setTransporterId] = useState('');
  const [distanceKm, setDistanceKm] = useState('100');

  // Generation state (Step 3)
  const [generating, setGenerating] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [results, setResults] = useState<EwbGenerateResult[]>([]);
  const [done, setDone] = useState(false);

  // Data fetching
  const [rawInvoices, setRawInvoices] = useState<any[]>([]);
  const [rawChallans, setRawChallans] = useState<any[]>([]);
  const [loadingDocs, setLoadingDocs] = useState(false);
  const { selectedWarehouseId, warehouses } = useWarehouseStore();

  const activeWarehouseName = useMemo(() => {
    if (!selectedWarehouseId || selectedWarehouseId === 'ALL') return null;
    return (warehouses || []).find((w) => w.id === selectedWarehouseId)?.name || null;
  }, [selectedWarehouseId, warehouses]);

  const loadPendingDocs = useCallback(async () => {
    setLoadingDocs(true);
    try {
      if (docType === 'invoice') {
        const res = await complianceApi.searchInvoices('', selectedWarehouseId, true, false, false);
        const items = res?.data?.items || res?.data || res || [];
        if (Array.isArray(items)) setRawInvoices(items);
      } else {
        const res = await complianceApi.searchChallans('', selectedWarehouseId);
        const items = res?.data?.items || res?.data || res || [];
        if (Array.isArray(items)) setRawChallans(items);
      }
    } catch (err) {
      console.error('Failed to load pending documents for bulk EWB:', err);
    } finally {
      setLoadingDocs(false);
    }
  }, [docType, selectedWarehouseId]);

  useEffect(() => {
    loadPendingDocs();
  }, [loadPendingDocs]);

  // Normalized list of available documents
  const availableDocs = useMemo(() => {
    const sourceList = docType === 'invoice' ? rawInvoices : rawChallans;
    return sourceList.map((doc) => {
      const docNo = doc.invoice_number || doc.billNo || doc.challan_number || doc.challanNo || doc.invoiceNo || 'DOC';
      const partyName = doc.customer_name || doc.partyName || doc.name || 'Customer / Recipient';
      const customerGstin = (doc.customer_gstin || doc.customerGstin || doc.gstin || '').trim();
      const date = doc.invoice_date || doc.challan_date || doc.date || '';
      const grandTotal = Number(doc.total_amount || doc.grandTotal || doc.total || 0);
      const itemCount = Number(doc.item_count || doc.itemCount || (doc.items ? doc.items.length : 1));
      const address = doc.billing_address || doc.customer_address || doc.delivery_address || '';
      const stateCode = doc.state_code || doc.stateCode || (customerGstin.length >= 2 ? parseInt(customerGstin.slice(0, 2), 10) : null);
      const pincode = doc.pincode || doc.shipping_pincode || (address.match(/\b\d{6}\b/) ? address.match(/\b\d{6}\b/)[0] : '');

      return {
        id: doc.id || docNo,
        rawDoc: doc,
        docNo,
        partyName,
        customerGstin,
        date,
        grandTotal,
        itemCount,
        address,
        stateCode,
        pincode,
        hasEwb: Boolean(doc.has_eway_bill || doc.hasEwayBill || doc.ewb_number || doc.ewbNo),
      };
    }).filter((d) => !d.hasEwb);
  }, [docType, rawInvoices, rawChallans]);

  const filteredDocs = useMemo(() => {
    let list = availableDocs;
    if (onlyAbove50k) {
      list = list.filter((d) => d.grandTotal >= 50000);
    }
    if (dateFrom) {
      list = list.filter((d) => d.date >= dateFrom);
    }
    if (dateTo) {
      list = list.filter((d) => d.date <= dateTo);
    }
    if (customerSearch.trim()) {
      const q = customerSearch.toLowerCase().trim();
      list = list.filter(
        (d) =>
          d.partyName.toLowerCase().includes(q) ||
          d.docNo.toLowerCase().includes(q) ||
          d.customerGstin.toLowerCase().includes(q)
      );
    }
    return list;
  }, [availableDocs, onlyAbove50k, dateFrom, dateTo, customerSearch]);

  const selectedDocs = useMemo(() => {
    return filteredDocs.filter((d) => selectedIds.has(d.id));
  }, [filteredDocs, selectedIds]);

  const selectedTotal = useMemo(() => {
    return selectedDocs.reduce((s, d) => s + d.grandTotal, 0);
  }, [selectedDocs]);

  const allSelected = filteredDocs.length > 0 && selectedIds.size === filteredDocs.length;

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredDocs.map((d) => d.id)));
    }
  };

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const estimatedValidityDays = useMemo(() => {
    const km = parseInt(distanceKm, 10);
    if (isNaN(km) || km <= 0) return 1;
    return Math.max(1, Math.ceil(km / 200));
  }, [distanceKm]);

  // Bulk Generate Execution with full Whitebooks payload & validation
  const handleGenerateAll = useCallback(() => {
    const toGenerate = selectedDocs;
    if (toGenerate.length === 0) return;

    setGenerating(true);
    setProgress({ current: 0, total: toGenerate.length });
    setResults([]);

    const genResults: EwbGenerateResult[] = [];

    const processNext = async (idx: number) => {
      if (idx >= toGenerate.length) {
        setGenerating(false);
        setDone(true);
        setResults(genResults);
        return;
      }

      const item = toGenerate[idx];

      // 1. Fetch full details for the specific invoice to guarantee exact HSNs, rates, amounts & address
      let fullDoc = item.rawDoc;
      if (item.id && docType === 'invoice') {
        const detailRes = await complianceApi.getInvoiceById(item.id).catch(() => null);
        if (detailRes?.success && detailRes?.data) {
          fullDoc = detailRes.data;
        }
      }

      // 2. Extract address, pincode & state
      const customerAddress = fullDoc.billing_address || fullDoc.customer_address || fullDoc.delivery_address || item.address || '';
      const toPincode = fullDoc.shipping_pincode || fullDoc.pincode || item.pincode || (customerAddress.match(/\b\d{6}\b/) ? customerAddress.match(/\b\d{6}\b/)[0] : '400001');
      const customerGstin = (fullDoc.customer_gstin || fullDoc.customerGstin || fullDoc.gstin || item.customerGstin || '').trim();
      const toStateCode = fullDoc.state_code || fullDoc.stateCode || item.stateCode || (customerGstin.length >= 2 ? parseInt(customerGstin.slice(0, 2), 10) : 27);

      // 3. Extract items with Whitebooks / NIC HSN & Tax mapping
      const rawItems = Array.isArray(fullDoc.items) && fullDoc.items.length > 0 ? fullDoc.items : [];
      const itemList = rawItems.length > 0
        ? rawItems.map((it: any) => {
            const hsnStr = String(it.hsn_code || it.hsnCode || it.hsn || '8471').replace(/\D/g, '');
            const hsnCode = hsnStr.length >= 6 ? Number(hsnStr) : Number(hsnStr.padEnd(6, '0'));
            const taxableAmt = Number(it.taxable_amount || it.taxableAmount || (Number(it.rate || it.unitPrice || 0) * Number(it.quantity || it.qty || 1)) || 1000);
            const cgstRate = Number(it.cgstRate || it.cgst_rate || (toStateCode === 27 ? 9 : 0));
            const sgstRate = Number(it.sgstRate || it.sgst_rate || (toStateCode === 27 ? 9 : 0));
            const igstRate = Number(it.igstRate || it.igst_rate || (toStateCode === 27 ? 0 : 18));

            return {
              productName:   it.item_name || it.itemName || it.name || 'Goods',
              productDesc:   it.description || it.item_name || it.itemName || 'Goods',
              hsnCode,
              quantity:      Number(it.quantity || it.qty || 1),
              qtyUnit:       String(it.unit || it.qtyUnit || 'NOS').slice(0, 3).toUpperCase(),
              taxableAmount: taxableAmt,
              cgstRate,
              sgstRate,
              igstRate,
              cessRate:      Number(it.cess_rate || it.cessRate || 0),
            };
          })
        : [{
            productName:   'Consolidated Goods',
            productDesc:   'Goods under consignment',
            hsnCode:       847100,
            quantity:      1,
            qtyUnit:       'NOS',
            taxableAmount: item.grandTotal > 0 ? item.grandTotal : 50000,
            cgstRate:      toStateCode === 27 ? 9 : 0,
            sgstRate:      toStateCode === 27 ? 9 : 0,
            igstRate:      toStateCode === 27 ? 0 : 18,
            cessRate:      0,
          }];

      const rawDocStr = String(item.docNo || '').trim();
      const cleanDocNo = rawDocStr.length <= 16
        ? rawDocStr
        : rawDocStr.replace(/[^a-zA-Z0-9]/g, '').slice(-16);

      const payload = {
        docNo:           cleanDocNo,
        docType:         docType === 'challan' ? 'CHL' : 'INV',
        supplyType:      'O',
        subSupplyType:   '1',
        transactionType: 1,

        toGstin:         customerGstin || 'URP',
        toTrdName:       fullDoc.customer_name || fullDoc.partyName || item.partyName || 'Customer',
        toAddr1:         customerAddress.slice(0, 60) || 'Main Street, Industrial Area',
        toAddr2:         '',
        toPlace:         customerAddress.split(',')[0]?.trim() || 'Mumbai',
        toPincode:       parseInt(toPincode, 10) || 400001,
        toStateCode,
        actToStateCode:  toStateCode,

        transMode:       transportMode || '1',
        transDistance:   distanceKm && Number(distanceKm) > 0 ? distanceKm : '100',
        vehicleNo:       (vehicleNumber || '').replace(/\s+/g, '').toUpperCase(),
        vehicleType:     vehicleType || 'R',
        transporterName: transporterName || '',
        transporterId:   transporterId || '',
        transDocNo:      '',
        transDocDate:    '',

        invoiceId:       docType === 'invoice' ? item.id : null,
        challanId:       docType === 'challan' ? item.id : null,
        invoiceNumber:   item.docNo,
        customerName:    item.partyName,
        distanceKm:      distanceKm || '100',

        itemList,
      };

      try {
        const res = await complianceApi.generateEWayBill(payload);
        if (res?.success && res?.data) {
          const ewbNo = res.data.ewbNumber || res.data.ewbNo || res.data.id || 'EWB';
          genResults.push({
            id: item.id,
            docNo: item.docNo,
            customer: item.partyName,
            status: 'SUCCESS',
            ewbNumber: String(ewbNo),
            validUntil: res.data.validUpto || res.data.validUntil || '',
            error: '',
          });
        } else {
          // Extract NIC error code and human explanation from Whitebooks validation
          let errorMsg = res?.message || 'EWB registration failed on Government NIC portal';
          if (Array.isArray(res?.errors) && res.errors.length > 0) {
            errorMsg = res.errors.map((e: any) => `[${e.code || 'NIC'}] ${e.message}${e.fix ? ' (' + e.fix + ')' : ''}`).join(' | ');
          }
          genResults.push({
            id: item.id,
            docNo: item.docNo,
            customer: item.partyName,
            status: 'FAILED',
            ewbNumber: '',
            error: errorMsg,
          });
        }
      } catch (err: any) {
        genResults.push({
          id: item.id,
          docNo: item.docNo,
          customer: item.partyName,
          status: 'FAILED',
          ewbNumber: '',
          error: err?.message || 'Network error communicating with E-Way Bill API',
        });
      }

      setProgress({ current: idx + 1, total: toGenerate.length });
      setTimeout(() => processNext(idx + 1), 350);
    };

    processNext(0);
  }, [selectedDocs, docType, transportMode, distanceKm, vehicleNumber, vehicleType, transporterName, transporterId, onGenerated]);

  const successCount = results.filter((r) => r.status === 'SUCCESS').length;
  const failedCount = results.filter((r) => r.status === 'FAILED').length;

  const handleCopy = (text: string) => {
    if (!text) return;
    navigator.clipboard.writeText(text).catch(() => {});
  };

  const handleClose = () => {
    if (results.some((r) => r.status === 'SUCCESS')) {
      onGenerated?.();
    }
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-3 sm:p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-xs" onClick={!generating ? handleClose : undefined} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[92vh] flex flex-col overflow-hidden border border-gray-100">
        
        {/* Header */}
        <div className="shrink-0 px-6 py-4 border-b border-gray-200 flex items-center justify-between bg-white">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-indigo-50 border border-indigo-100 flex items-center justify-center text-[#4f46e5]">
              <i className="ri-truck-line text-lg" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-base font-bold text-gray-900">Bulk E-Way Bill Generation</h2>
                {activeWarehouseName && (
                  <span className="text-[10px] bg-slate-100 text-slate-700 font-medium px-2 py-0.5 rounded-md border border-slate-200">
                    {activeWarehouseName}
                  </span>
                )}
              </div>
              <p className="text-xs text-gray-500">Batch generate E-Way Bills directly on NIC portal</p>
            </div>
          </div>
          <button
            onClick={!generating ? handleClose : undefined}
            disabled={generating}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-700 transition-colors disabled:opacity-30 cursor-pointer"
          >
            <i className="ri-close-line text-xl" />
          </button>
        </div>

        {/* Step Indicators */}
        <div className="shrink-0 px-6 py-3 bg-slate-50 border-b border-gray-200">
          <div className="flex items-center justify-between max-w-lg mx-auto">
            {([1, 2, 3] as Step[]).map((s, i) => (
              <div key={s} className="flex items-center gap-2">
                <div
                  className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-all ${
                    s < step
                      ? 'bg-emerald-600 text-white shadow-xs'
                      : s === step
                      ? 'bg-[#4f46e5] text-white ring-4 ring-indigo-100 shadow-xs'
                      : 'bg-gray-200 text-gray-500'
                  }`}
                >
                  {s < step ? <i className="ri-check-line text-sm" /> : s}
                </div>
                <span className={`text-xs font-semibold ${s === step ? 'text-indigo-600 font-bold' : s < step ? 'text-gray-800' : 'text-gray-400'}`}>
                  {s === 1 ? 'Select Documents' : s === 2 ? 'Transport Details' : 'Review & Generate'}
                </span>
                {i < 2 && <div className={`w-8 sm:w-12 h-0.5 ${s < step ? 'bg-emerald-500' : 'bg-gray-200'}`} />}
              </div>
            ))}
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-4 bg-white">
          
          {/* STEP 1: Select Invoices or Challans */}
          {step === 1 && (
            <div className="space-y-4">
              {/* Document Type Switcher */}
              <div className="flex items-center justify-between gap-3 pb-1 border-b border-gray-100">
                <div className="flex items-center gap-2 bg-gray-100 p-1 rounded-lg">
                  <button
                    onClick={() => { setDocType('invoice'); setSelectedIds(new Set()); }}
                    className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-all cursor-pointer ${
                      docType === 'invoice' ? 'bg-white text-indigo-600 shadow-xs font-bold' : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    <i className="ri-file-text-line mr-1" /> Sales Invoices
                  </button>
                  <button
                    onClick={() => { setDocType('challan'); setSelectedIds(new Set()); }}
                    className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-all cursor-pointer ${
                      docType === 'challan' ? 'bg-white text-indigo-600 shadow-xs font-bold' : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    <i className="ri-truck-line mr-1" /> Delivery Challans
                  </button>
                </div>

                <label className="flex items-center gap-2 text-xs font-medium text-gray-700 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={onlyAbove50k}
                    onChange={(e) => setOnlyAbove50k(e.target.checked)}
                    className="w-3.5 h-3.5 rounded border-gray-300 text-[#4f46e5] focus:ring-indigo-500 cursor-pointer"
                  />
                  <span>Mandatory Threshold ( ≥ ₹50,000 only)</span>
                </label>
              </div>

              {/* Filters */}
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
                <div className="relative flex-1">
                  <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
                  <input
                    type="text"
                    value={customerSearch}
                    onChange={(e) => setCustomerSearch(e.target.value)}
                    placeholder="Search by customer name, doc #, or GSTIN..."
                    className="w-full pl-9 pr-3 py-2 text-xs sm:text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4f46e5]/20 focus:border-[#4f46e5] bg-white shadow-xs"
                  />
                  {customerSearch && (
                    <button onClick={() => setCustomerSearch('')} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                      <i className="ri-close-circle-fill text-sm" />
                    </button>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="date"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                    title="From date"
                    className="px-3 py-2 text-xs sm:text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4f46e5]/20 focus:border-[#4f46e5] bg-white shadow-xs"
                  />
                  <span className="text-gray-400 text-xs">to</span>
                  <input
                    type="date"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                    title="To date"
                    className="px-3 py-2 text-xs sm:text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4f46e5]/20 focus:border-[#4f46e5] bg-white shadow-xs"
                  />
                  {(dateFrom || dateTo) && (
                    <button
                      onClick={() => { setDateFrom(''); setDateTo(''); }}
                      className="p-2 text-xs text-gray-500 hover:text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                      title="Clear date filters"
                    >
                      <i className="ri-filter-off-line" />
                    </button>
                  )}
                  <button
                    onClick={loadPendingDocs}
                    disabled={loadingDocs}
                    className="p-2 text-xs text-gray-600 hover:text-indigo-600 bg-gray-100 hover:bg-indigo-50 border border-gray-200 rounded-lg transition-colors cursor-pointer"
                    title="Refresh list"
                  >
                    <i className={`ri-refresh-line text-sm ${loadingDocs ? 'animate-spin' : ''}`} />
                  </button>
                </div>
              </div>

              {/* Table */}
              <div className="border border-gray-200 rounded-xl overflow-hidden shadow-xs">
                <div className="max-h-[380px] overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 z-10 bg-slate-50 border-b border-gray-200">
                      <tr>
                        <th className="px-3 py-3 w-10 text-center">
                          <input
                            type="checkbox"
                            checked={allSelected}
                            onChange={toggleSelectAll}
                            className="w-3.5 h-3.5 rounded border-gray-300 text-[#4f46e5] focus:ring-indigo-500 cursor-pointer"
                          />
                        </th>
                        <th className="px-3 py-3 text-left font-bold text-gray-600 uppercase tracking-wider">Doc No</th>
                        <th className="px-3 py-3 text-left font-bold text-gray-600 uppercase tracking-wider">Customer / Recipient</th>
                        <th className="px-3 py-3 text-left font-bold text-gray-600 uppercase tracking-wider">GSTIN</th>
                        <th className="px-3 py-3 text-left font-bold text-gray-600 uppercase tracking-wider">Date</th>
                        <th className="px-3 py-3 text-right font-bold text-gray-600 uppercase tracking-wider">Consignment Value</th>
                        <th className="px-3 py-3 text-center font-bold text-gray-600 uppercase tracking-wider">Items</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 bg-white">
                      {loadingDocs ? (
                        <tr>
                          <td colSpan={7} className="px-3 py-12 text-center text-gray-400">
                            <i className="ri-loader-4-line animate-spin text-2xl text-indigo-600 mb-2 block mx-auto" />
                            Loading pending {docType === 'invoice' ? 'sales invoices' : 'delivery challans'}...
                          </td>
                        </tr>
                      ) : filteredDocs.length === 0 ? (
                        <tr>
                          <td colSpan={7} className="px-3 py-12 text-center text-gray-400">
                            <i className="ri-inbox-line text-3xl text-gray-300 mb-2 block mx-auto" />
                            No pending {docType === 'invoice' ? 'invoices' : 'challans'} found for E-Way Bill generation.
                          </td>
                        </tr>
                      ) : (
                        filteredDocs.map((doc) => {
                          const isChecked = selectedIds.has(doc.id);
                          return (
                            <tr
                              key={doc.id}
                              onClick={() => toggleSelect(doc.id)}
                              className={`hover:bg-indigo-50/40 cursor-pointer transition-colors ${
                                isChecked ? 'bg-indigo-50/70 font-medium' : ''
                              }`}
                            >
                              <td className="px-3 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                                <input
                                  type="checkbox"
                                  checked={isChecked}
                                  onChange={() => toggleSelect(doc.id)}
                                  className="w-3.5 h-3.5 rounded border-gray-300 text-[#4f46e5] focus:ring-indigo-500 cursor-pointer"
                                />
                              </td>
                              <td className="px-3 py-3 font-mono font-bold text-indigo-600 whitespace-nowrap">
                                {doc.docNo}
                              </td>
                              <td className="px-3 py-3 text-gray-900 max-w-[200px] truncate">
                                {doc.partyName}
                              </td>
                              <td className="px-3 py-3 font-mono text-gray-600 whitespace-nowrap">
                                {doc.customerGstin || 'URP (Unregistered)'}
                              </td>
                              <td className="px-3 py-3 text-gray-600 whitespace-nowrap">
                                {formatDate(doc.date)}
                              </td>
                              <td className="px-3 py-3 text-right font-semibold text-gray-900 whitespace-nowrap">
                                {formatINR(doc.grandTotal)}
                              </td>
                              <td className="px-3 py-3 text-center text-gray-500 whitespace-nowrap">
                                {doc.itemCount}
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Bottom bar */}
              <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-2">
                <div className="text-xs text-gray-600 flex items-center gap-3">
                  <span className="font-semibold text-gray-900 bg-gray-100 px-2.5 py-1 rounded-md">
                    {selectedDocs.length} of {filteredDocs.length} selected
                  </span>
                  <span>
                    Total Consignment Value: <strong className="text-indigo-700">{formatINR(selectedTotal)}</strong>
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={onClose}
                    className="px-4 py-2 text-xs font-semibold text-gray-600 hover:text-gray-800 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors cursor-pointer"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => setStep(2)}
                    disabled={selectedDocs.length === 0}
                    className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-semibold text-white bg-[#4f46e5] rounded-lg hover:bg-indigo-600 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer shadow-xs transition-colors"
                  >
                    Configure Transport ({selectedDocs.length}) <i className="ri-arrow-right-line" />
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* STEP 2: Transport Details */}
          {step === 2 && (
            <div className="space-y-5">
              <div className="bg-indigo-50/70 border border-indigo-200 rounded-xl p-4 flex items-center justify-between">
                <div>
                  <h4 className="text-xs font-bold text-indigo-900 uppercase tracking-wider">Batch Transport Configuration</h4>
                  <p className="text-xs text-indigo-700 mt-0.5">
                    These transport and vehicle details will be attached to all {selectedDocs.length} generated E-Way Bills.
                  </p>
                </div>
                <div className="text-right">
                  <span className="text-xs font-bold font-mono text-indigo-900 bg-white/80 px-2.5 py-1 rounded-md border border-indigo-200">
                    {selectedDocs.length} Consignments
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Transport Mode</label>
                  <select
                    value={transportMode}
                    onChange={(e) => setTransportMode(e.target.value)}
                    className="w-full px-3 py-2 text-xs sm:text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4f46e5]/20 focus:border-[#4f46e5] cursor-pointer bg-white"
                  >
                    <option value="1">1 — Road Transport</option>
                    <option value="2">2 — Rail Transport</option>
                    <option value="3">3 — Air Cargo</option>
                    <option value="4">4 — Ship / Sea Cargo</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">
                    Vehicle Number <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={vehicleNumber}
                    onChange={(e) => setVehicleNumber(e.target.value.toUpperCase())}
                    placeholder="e.g. MH04AB1234 or DL01XY9876"
                    className="w-full px-3 py-2 text-xs sm:text-sm font-mono border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4f46e5]/20 focus:border-[#4f46e5] uppercase"
                  />
                  <p className="text-[10px] text-gray-400 mt-1">Format: State Code + RTO + Series + Digits without special characters</p>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Vehicle Type</label>
                  <select
                    value={vehicleType}
                    onChange={(e) => setVehicleType(e.target.value as 'R' | 'O')}
                    className="w-full px-3 py-2 text-xs sm:text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4f46e5]/20 focus:border-[#4f46e5] cursor-pointer bg-white"
                  >
                    <option value="R">Regular Vehicle (Standard 200 km/day)</option>
                    <option value="O">Over Dimensional Cargo (ODC - 20 km/day)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Approximate Distance (km)</label>
                  <input
                    type="number"
                    value={distanceKm}
                    onChange={(e) => setDistanceKm(e.target.value)}
                    placeholder="e.g. 150"
                    className="w-full px-3 py-2 text-xs sm:text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4f46e5]/20 focus:border-[#4f46e5]"
                  />
                  <p className="text-[10px] text-indigo-600 font-medium mt-1">
                    Calculated Statutory Validity: <strong>{estimatedValidityDays} Day{estimatedValidityDays > 1 ? 's' : ''}</strong>
                  </p>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Transporter Name (Optional)</label>
                  <input
                    type="text"
                    value={transporterName}
                    onChange={(e) => setTransporterName(e.target.value)}
                    placeholder="e.g. VRL Logistics / SafeXpress"
                    className="w-full px-3 py-2 text-xs sm:text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4f46e5]/20 focus:border-[#4f46e5]"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Transporter ID / GSTIN (Optional)</label>
                  <input
                    type="text"
                    value={transporterId}
                    onChange={(e) => setTransporterId(e.target.value.toUpperCase())}
                    placeholder="15-digit GSTIN or TRANSIN"
                    className="w-full px-3 py-2 text-xs sm:text-sm font-mono border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4f46e5]/20 focus:border-[#4f46e5] uppercase"
                  />
                </div>
              </div>

              <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                <button
                  onClick={() => setStep(1)}
                  className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-semibold text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 cursor-pointer transition-colors"
                >
                  <i className="ri-arrow-left-line" /> Back to Document Selection
                </button>
                <button
                  onClick={() => setStep(3)}
                  disabled={!vehicleNumber.trim()}
                  className="inline-flex items-center gap-1.5 px-5 py-2 text-xs font-semibold text-white bg-[#4f46e5] rounded-lg hover:bg-indigo-600 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer shadow-xs transition-colors"
                >
                  Review & Generate ({selectedDocs.length}) <i className="ri-arrow-right-line" />
                </button>
              </div>
            </div>
          )}

          {/* STEP 3: Review & Generate */}
          {step === 3 && (
            <div className="space-y-4">
              {!generating && !done && (
                <>
                  <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                    <div>
                      <h4 className="text-xs font-bold text-gray-900 uppercase tracking-wider">Final Verification</h4>
                      <p className="text-xs text-gray-500 mt-0.5">
                        Ready to generate {selectedDocs.length} E-Way Bills with Vehicle <strong className="font-mono text-indigo-600">{vehicleNumber}</strong> ({estimatedValidityDays} days validity).
                      </p>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-bold font-mono text-indigo-600">{formatINR(selectedTotal)}</div>
                      <div className="text-[11px] text-gray-500">{selectedDocs.length} Documents</div>
                    </div>
                  </div>

                  <div className="border border-gray-200 rounded-xl overflow-hidden shadow-xs">
                    <div className="max-h-[300px] overflow-y-auto">
                      <table className="w-full text-xs">
                        <thead className="sticky top-0 bg-slate-50 border-b border-gray-200">
                          <tr>
                            <th className="px-3 py-2.5 text-left font-bold text-gray-600">Doc No</th>
                            <th className="px-3 py-2.5 text-left font-bold text-gray-600">Customer</th>
                            <th className="px-3 py-2.5 text-left font-bold text-gray-600">GSTIN</th>
                            <th className="px-3 py-2.5 text-right font-bold text-gray-600">Value</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 bg-white">
                          {selectedDocs.map((doc) => (
                            <tr key={doc.id}>
                              <td className="px-3 py-2.5 font-mono font-bold text-indigo-600">{doc.docNo}</td>
                              <td className="px-3 py-2.5 text-gray-700">{doc.partyName}</td>
                              <td className="px-3 py-2.5 font-mono text-gray-600">{doc.customerGstin || 'URP'}</td>
                              <td className="px-3 py-2.5 text-right font-bold text-gray-900">{formatINR(doc.grandTotal)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </>
              )}

              {generating && !done && (
                <div className="text-center py-10 space-y-4">
                  <div className="w-12 h-12 rounded-full bg-indigo-50 border border-indigo-200 flex items-center justify-center mx-auto text-indigo-600">
                    <i className="ri-loader-4-line animate-spin text-2xl" />
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-gray-900">
                      Generating E-Way Bills with Government Portal ({progress.current} of {progress.total})
                    </h3>
                    <p className="text-xs text-gray-500 mt-1">Please do not close this window while generating...</p>
                  </div>
                  <div className="max-w-md mx-auto bg-gray-200 rounded-full h-2.5 overflow-hidden shadow-inner">
                    <div
                      className="h-full bg-gradient-to-r from-indigo-500 to-indigo-600 rounded-full transition-all duration-300"
                      style={{ width: `${progress.total > 0 ? (progress.current / progress.total) * 100 : 0}%` }}
                    />
                  </div>
                </div>
              )}

              {done && (
                <div className="space-y-4">
                  <div className={`p-4 rounded-xl border flex items-center gap-3 ${
                    failedCount > 0
                      ? 'bg-amber-50 border-amber-300 text-amber-900'
                      : 'bg-emerald-50 border-emerald-300 text-emerald-900'
                  }`}>
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                      failedCount > 0 ? 'bg-amber-200 text-amber-800' : 'bg-emerald-200 text-emerald-800'
                    }`}>
                      <i className={`text-lg ${failedCount > 0 ? 'ri-error-warning-line' : 'ri-checkbox-circle-line'}`} />
                    </div>
                    <div>
                      <h4 className="text-xs font-bold">
                        Batch Complete: {successCount} Generated {failedCount > 0 ? `, ${failedCount} Failed` : ''}
                      </h4>
                      <p className="text-[11px] opacity-90 mt-0.5">
                        E-Way Bills have been created and synced with the Government system.
                      </p>
                    </div>
                  </div>

                  <div className="border border-gray-200 rounded-xl overflow-hidden shadow-xs">
                    <div className="max-h-[320px] overflow-y-auto">
                      <table className="w-full text-xs">
                        <thead className="sticky top-0 bg-slate-50 border-b border-gray-200">
                          <tr>
                            <th className="px-3 py-2.5 text-left font-bold text-gray-600">Doc No</th>
                            <th className="px-3 py-2.5 text-left font-bold text-gray-600">Status</th>
                            <th className="px-3 py-2.5 text-left font-bold text-gray-600">E-Way Bill No</th>
                            <th className="px-3 py-2.5 text-left font-bold text-gray-600">Details / Error</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 bg-white">
                          {results.map((r, i) => (
                            <tr key={i}>
                              <td className="px-3 py-2.5 font-mono font-bold text-indigo-600">{r.docNo}</td>
                              <td className="px-3 py-2.5">
                                <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold inline-flex items-center gap-1 ${
                                  r.status === 'SUCCESS'
                                    ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                                    : 'bg-red-50 text-red-600 border border-red-200'
                                }`}>
                                  {r.status === 'SUCCESS' ? '✓ Generated' : '✗ Failed'}
                                </span>
                              </td>
                              <td className="px-3 py-2.5 font-mono text-gray-900 font-semibold">
                                {r.ewbNumber ? (
                                  <div className="flex items-center gap-1.5">
                                    <span>{r.ewbNumber}</span>
                                    <button
                                      onClick={() => handleCopy(r.ewbNumber)}
                                      title="Copy EWB"
                                      className="text-gray-400 hover:text-indigo-600 cursor-pointer"
                                    >
                                      <i className="ri-file-copy-line" />
                                    </button>
                                  </div>
                                ) : (
                                  '—'
                                )}
                              </td>
                              <td className="px-3 py-2.5 text-gray-600">
                                {r.error ? <span className="text-red-600">{r.error}</span> : <span className="text-emerald-700 font-mono">Valid Until: {formatDate(r.validUntil)}</span>}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex items-center justify-between pt-2">
                {!generating && !done ? (
                  <>
                    <button
                      onClick={() => setStep(2)}
                      className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-semibold text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 cursor-pointer transition-colors"
                    >
                      <i className="ri-arrow-left-line" /> Back to Transport
                    </button>
                    <button
                      onClick={handleGenerateAll}
                      className="inline-flex items-center gap-1.5 px-5 py-2 text-xs font-bold text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 cursor-pointer shadow-xs transition-colors"
                    >
                      <i className="ri-truck-line text-sm" /> Generate All {selectedDocs.length} E-Way Bills
                    </button>
                  </>
                ) : done ? (
                  <div className="flex justify-end w-full">
                    <button
                      onClick={() => {
                        onGenerated?.();
                        onClose();
                      }}
                      className="inline-flex items-center gap-1.5 px-5 py-2 text-xs font-bold text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 cursor-pointer shadow-xs transition-colors"
                    >
                      Done & View E-Way Bills
                    </button>
                  </div>
                ) : null}
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}